import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import {
  BeaconDepositStatus,
  DepositQueueStatus,
  getDepositQueueStatus,
} from '../../utils/depositQueueTracker';
import {
  BEACON_RPC_URL,
  RPC_URL,
  CONTRACT_ADDRESS,
  DEPOSIT_STATUS_POLL_INTERVAL,
} from '../../utils/envVars';

interface ColorShade {
  light?: string;
  medium?: string;
  dark?: string;
}

interface ThemeColors {
  green: ColorShade;
  yellow: ColorShade;
  blue: ColorShade;
  red: ColorShade;
  gray: ColorShade & { lightest?: string; darkish?: string };
}

type StatusVariant = 'success' | 'warning' | 'error' | 'info';

const getVariantColor = (
  theme: ThemeColors,
  variant: StatusVariant
): string => {
  switch (variant) {
    case 'success':
      return theme.green.dark || '#26AB83';
    case 'warning':
      return theme.yellow.dark || '#f0ad4d';
    case 'error':
      return theme.red.medium || '#912d2c';
    case 'info':
    default:
      return theme.blue.dark || '#0F2A43';
  }
};

const StatusContainer = styled.div<{ variant: StatusVariant }>`
  padding: 16px;
  border-radius: 8px;
  margin: 8px 0;
  background-color: ${p => getVariantColor(p.theme, p.variant)}20;
  border: 1px solid ${p => getVariantColor(p.theme, p.variant)};
`;

const StatusTitle = styled.div`
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDetail = styled.div`
  font-size: 14px;
  color: ${p => p.theme.gray?.light || '#fbfbfb'};
  margin-top: 4px;
`;

const QueuePosition = styled.span`
  font-weight: bold;
  font-size: 18px;
  color: ${p => p.theme.blue?.light || '#b0e2f5'};
`;

const ValidatorIndex = styled.span`
  font-weight: bold;
  color: ${p => p.theme.green?.light || '#F8FAF9'};
`;

const LoadingDots = styled.span`
  &::after {
    content: '...';
    animation: dots 1.5s steps(4, end) infinite;
  }

  @keyframes dots {
    0%,
    20% {
      content: '';
    }
    40% {
      content: '.';
    }
    60% {
      content: '..';
    }
    80%,
    100% {
      content: '...';
    }
  }
`;

interface DepositStatusTrackerProps {
  txHash: string;
  pubkey: string;
  onStatusUpdate?: (status: DepositQueueStatus) => void;
  pollInterval?: number;
}

export const DepositStatusTracker: React.FC<DepositStatusTrackerProps> = ({
  txHash,
  pubkey,
  onStatusUpdate,
  pollInterval = DEPOSIT_STATUS_POLL_INTERVAL,
}) => {
  const [status, setStatus] = useState<DepositQueueStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const onStatusUpdateRef = useRef(onStatusUpdate);

  // Keep ref updated with latest callback to avoid stale closures
  useEffect(() => {
    onStatusUpdateRef.current = onStatusUpdate;
  }, [onStatusUpdate]);

  const fetchStatus = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const result = await getDepositQueueStatus(txHash, pubkey, {
          beaconRpcUrl: BEACON_RPC_URL,
          executionRpcUrl: RPC_URL,
          depositContractAddress: CONTRACT_ADDRESS,
          signal,
        });

        // Check if aborted before updating state
        if (signal?.aborted) {
          return;
        }

        setStatus(result);
        setError(null);
        onStatusUpdateRef.current?.(result);

        // Stop polling if validator is active
        if (result.status === BeaconDepositStatus.ACTIVE) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV === 'development')
          console.error('Deposit status check failed:', err);
        setError('Error checking status');
      } finally {
        setIsLoading(false);
      }
    },
    [txHash, pubkey]
  );

  useEffect(() => {
    // Create abort controller for this effect lifecycle
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    fetchStatus(signal);

    // Set up polling only if pollInterval is positive
    if (pollInterval > 0) {
      intervalRef.current = setInterval(() => {
        fetchStatus(signal);
      }, pollInterval);
    }

    return () => {
      // Abort any in-flight requests
      abortControllerRef.current?.abort();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchStatus, pollInterval]);

  if (isLoading) {
    return (
      <StatusContainer variant="info">
        <StatusTitle>
          <LoadingDots>
            <FormattedMessage defaultMessage="Checking deposit status" />
          </LoadingDots>
        </StatusTitle>
      </StatusContainer>
    );
  }

  if (error) {
    return (
      <StatusContainer variant="error">
        <StatusTitle>
          <FormattedMessage defaultMessage="Error checking status" />
        </StatusTitle>
        <StatusDetail>{error}</StatusDetail>
      </StatusContainer>
    );
  }

  if (!status) {
    return null;
  }

  const renderStatus = () => {
    switch (status.status) {
      case BeaconDepositStatus.IN_QUEUE:
        return (
          <StatusContainer variant="info">
            <StatusTitle>
              <FormattedMessage defaultMessage="In Queue" />
            </StatusTitle>
            <StatusDetail>
              <FormattedMessage
                defaultMessage="{position} deposits ahead of yours"
                values={{
                  position: (
                    <QueuePosition>{status.queuePosition}</QueuePosition>
                  ),
                }}
              />
            </StatusDetail>
            {status.depositIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Deposit index: {index}"
                  values={{ index: status.depositIndex }}
                />
              </StatusDetail>
            )}
          </StatusContainer>
        );

      case BeaconDepositStatus.NEXT_IN_QUEUE:
        return (
          <StatusContainer variant="warning">
            <StatusTitle>
              <FormattedMessage defaultMessage="Next in Queue" />
            </StatusTitle>
            <StatusDetail>
              <FormattedMessage defaultMessage="Your deposit is next to be processed" />
            </StatusDetail>
          </StatusContainer>
        );

      case BeaconDepositStatus.PENDING_ACTIVATION:
        return (
          <StatusContainer variant="info">
            <StatusTitle>
              <FormattedMessage defaultMessage="Pending Activation" />
            </StatusTitle>
            {status.validatorIndex && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Validator index: {index}"
                  values={{
                    index: (
                      <ValidatorIndex>{status.validatorIndex}</ValidatorIndex>
                    ),
                  }}
                />
              </StatusDetail>
            )}
            <StatusDetail>
              <FormattedMessage defaultMessage="Waiting for beacon chain to activate your validator" />
            </StatusDetail>
          </StatusContainer>
        );

      case BeaconDepositStatus.ACTIVE:
        return (
          <StatusContainer variant="success">
            <StatusTitle>
              <FormattedMessage defaultMessage="Active" />
            </StatusTitle>
            {status.validatorIndex && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Validator index: {index}"
                  values={{
                    index: (
                      <ValidatorIndex>{status.validatorIndex}</ValidatorIndex>
                    ),
                  }}
                />
              </StatusDetail>
            )}
            <StatusDetail>
              <FormattedMessage defaultMessage="Your validator is now active and earning rewards" />
            </StatusDetail>
          </StatusContainer>
        );

      case BeaconDepositStatus.BEACON_UNAVAILABLE:
        return (
          <StatusContainer variant="warning">
            <StatusTitle>
              <FormattedMessage defaultMessage="Beacon Chain Unavailable" />
            </StatusTitle>
            <StatusDetail>
              <FormattedMessage defaultMessage="Unable to check beacon chain status. Your deposit is confirmed on the execution layer." />
            </StatusDetail>
            {status.depositIndex !== null && (
              <StatusDetail>
                <FormattedMessage
                  defaultMessage="Deposit index: {index}"
                  values={{ index: status.depositIndex }}
                />
              </StatusDetail>
            )}
          </StatusContainer>
        );

      case BeaconDepositStatus.TX_FAILED:
        return (
          <StatusContainer variant="error">
            <StatusTitle>
              <FormattedMessage defaultMessage="Transaction Failed" />
            </StatusTitle>
            <StatusDetail>
              <FormattedMessage defaultMessage="The deposit transaction failed or the deposit event was not found" />
            </StatusDetail>
          </StatusContainer>
        );

      case BeaconDepositStatus.UNKNOWN:
        return (
          <StatusContainer variant="info">
            <StatusTitle>
              <FormattedMessage defaultMessage="Checking status" />
            </StatusTitle>
          </StatusContainer>
        );

      default: {
        const exhaustiveCheck: never = status.status;
        return exhaustiveCheck;
      }
    }
  };

  return renderStatus();
};

export default DepositStatusTracker;
